import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
    Button,
    Modal,
    ModalComponent,
    ModalProps,
    PaginatedSelectField,
    Skeleton,
} from '@speeki/global-ui-components';

import { getPath } from '@services/RouteService/paths';

import { subsidiarySiteVar } from '@boot/reactiveVariables';
import { TagSelector } from '@domains/new-conversation/molecules/TagSelector/TagSelector';
import {
    CustomDescription,
    ModalDescription,
    SelectOptionTitle,
} from '@domains/new-conversation/organisms/SelectSubsidiarySitesModal/SelectSubsidiarySitesModal.styled';
import {
    SelectSubsidiarySiteModalValues,
    useSelectSubsidiarySitesModal,
} from '@domains/new-conversation/organisms/SelectSubsidiarySitesModal/useSelectSubsidiarySitesModal';

interface SelectSubsidiarySitesModalProps
    extends Pick<ModalProps, 'onClose' | 'open'> {
    cancelHandlingFunction?: () => void;
}

export const SelectSubsidiarySitesModal = ({
    cancelHandlingFunction,
    onClose,
    open,
}: SelectSubsidiarySitesModalProps) => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const {
        control,
        errors,
        handleSubmit,
        investigationEntityField,
        isValid,
        reset,
        setValue,
        subsidiarySettingsLoading,
        subsidiarySiteChoice,
        subsidiarySiteCopy,
        subsidiarySiteSelect,
        subsidiarySitesField,
        tenantUuid,
    } = useSelectSubsidiarySitesModal();

    const continueHandlingFunction = (
        data: SelectSubsidiarySiteModalValues,
    ) => {
        navigate(getPath({ path: '/new-conversation' }));
        subsidiarySiteVar({
            ...data,
            tenantUuid: tenantUuid || '',
        });

        onClose();
    };

    const handleCancel = () => {
        reset();
        navigate(getPath({ path: '/' }));
        onClose();
    };

    const customCopy = () => {
        if (subsidiarySettingsLoading)
            return (
                <CustomDescription>
                    <Skeleton height={40} />
                </CustomDescription>
            );

        if (subsidiarySiteCopy)
            return (
                <CustomDescription
                    dangerouslySetInnerHTML={{
                        __html: subsidiarySiteCopy,
                    }}
                />
            );

        return (
            <ModalDescription>
                {t('SELECT_SUBSIDIARY_SITES_DESCRIPTION')}
            </ModalDescription>
        );
    };

    const chosenEntity = useWatch({ control, name: 'subsidiarySite' });
    const isHqSelected = chosenEntity?.value === 'Headquarters';

    useEffect(() => {
        if (subsidiarySiteChoice && isHqSelected) {
            setValue('investigationEntity', 'HEADQUARTERS');
        }
    }, [chosenEntity]);

    return (
        <ModalComponent
            onClose={onClose}
            open={open}
            preventCloseEsc
            preventCloseOutside
            width={648}
        >
            <Modal.Content>
                <Modal.TitleSecondary center>
                    {t('SELECT_SUBSIDIARY_PLACEHOLDER')}
                </Modal.TitleSecondary>

                {customCopy()}

                <PaginatedSelectField
                    {...subsidiarySitesField}
                    {...subsidiarySiteSelect}
                    closeMenuOnSelect
                    error={!!errors.subsidiarySite}
                    label={`${t('SELECT_SUBSIDIARY')} *`}
                    message={errors.subsidiarySite?.message}
                    placeholder={t('SELECT_SUBSIDIARY_PLACEHOLDER')}
                    testId="NM2T69k"
                />
                {subsidiarySiteChoice && (
                    <>
                        <SelectOptionTitle>
                            {`${t('SELECT_BUSINESS_UNIT_OPTIONS')} *`}
                        </SelectOptionTitle>
                        <TagSelector
                            {...investigationEntityField}
                            options={[
                                {
                                    disabled:
                                        subsidiarySiteChoice && isHqSelected,
                                    label: t('SELECT_BUSINESS_UNIT_OPTION_1'),
                                    value: 'LOCAL_OFFICE',
                                },
                                {
                                    label: t('SELECT_BUSINESS_UNIT_OPTION_2'),
                                    value: 'HEADQUARTERS',
                                },
                            ]}
                        />
                    </>
                )}
            </Modal.Content>
            <Modal.Footer>
                <Modal.ButtonsWrapper>
                    <Button
                        onClick={
                            cancelHandlingFunction
                                ? cancelHandlingFunction
                                : handleCancel
                        }
                        testId="button_cancel-business-unit"
                        variant={'secondary'}
                    >
                        {t('CANCEL')}
                    </Button>
                    <Button
                        disabled={!isValid}
                        onClick={handleSubmit(continueHandlingFunction)}
                        testId="button_continue-business-unit"
                        variant={'primary'}
                    >
                        {t('CONTINUE')}
                    </Button>
                </Modal.ButtonsWrapper>
            </Modal.Footer>
        </ModalComponent>
    );
};
